/**
 * @docs docs/features/analytics-event-catalog
 * @fix  docs/fixes/2026-09-07-analytics-catalog-drift-dropped-events.md
 * @rule The id list is re-exported from the shared contract, never restated here. The
 *       Website owns only the JsonRecord-typed payload contract its landing tracker sends.
 */

/*----------------------------------
- ANALYTICS EVENTS CATALOG
----------------------------------*/

/*
 * Everything the Website tracks is proxied to Product, which validates against the
 * shared list. Re-exporting that list keeps the client-side allowlist and the server-side
 * one identical by construction; the fix note above records the id that drifted while
 * this file kept a copy of its own.
 */

import {
    RETIRED_TRACKED_EVENT_METADATA,
    TRACKED_EVENT_IDS,
} from '@uniquedomains/components/contracts/analytics/events';
import type { JsonRecord } from '@/common/types/json';

export { RETIRED_TRACKED_EVENT_METADATA, TRACKED_EVENT_IDS };

export type TrackedEventId = (typeof TRACKED_EVENT_IDS)[number];

export type TrackedEventSource =
    | 'app'
    | 'landing'
    | 'conversion_modal'
    | 'investor_feed'
    | 'founder_domains'
    | 'server';

export type TrackedEventPayload = {
    event: TrackedEventId;
    value?: number;
    data?: JsonRecord;
};

export type RawTrackedEventPayload = {
    event: string;
    value?: number | string | null;
    data?: JsonRecord | null;
};

const TRACKED_EVENT_ID_SET = new Set<string>(TRACKED_EVENT_IDS);

export const isTrackedEventId = (value: string): value is TrackedEventId => TRACKED_EVENT_ID_SET.has(value);

const toFiniteNumber = (value: number | string | null | undefined): number | undefined => {
    const numeric = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(numeric) ? numeric : undefined;
};

const toPlainData = (value: JsonRecord | null | undefined): JsonRecord | undefined => value || undefined;

const withMergedData = (base: JsonRecord | undefined, extension: JsonRecord): JsonRecord => ({
    ...(base || {}),
    ...extension,
});

const normalizeInvestorFeedSetRange = (
    event: string,
    value: number | undefined,
    data: JsonRecord | undefined,
): TrackedEventPayload | null => {
    const match = event.match(/^investor_feed_set_range_(today|7d|30d)$/);
    if (!match) return null;

    const range = match[1];
    return {
        event: 'investor_feed_set_range',
        value,
        data: withMergedData(data, { range }),
    };
};

const normalizeInvestorFirstAction = (
    event: string,
    value: number | undefined,
    data: JsonRecord | undefined,
): TrackedEventPayload | null => {
    const match = event.match(/^investor_feed_first_action_(watch|pass|favorite|buy)_ms$/);
    if (!match) return null;

    const action = match[1];
    return {
        event: 'investor_feed_first_action_ms',
        value,
        data: withMergedData(data, {
            action,
            latency_ms: value,
        }),
    };
};

const normalizeFounderFirstAction = (
    event: string,
    value: number | undefined,
    data: JsonRecord | undefined,
): TrackedEventPayload | null => {
    const match = event.match(/^founder_domains_first_action_(shortlist|pass|buy|more_like_this)_ms$/);
    if (!match) return null;

    const action = match[1];
    return {
        event: 'founder_domains_first_action_ms',
        value,
        data: withMergedData(data, {
            action,
            latency_ms: value,
        }),
    };
};

/**
 * Normalizes raw event inputs into a strict, stable payload accepted by storage.
 * Returns `null` when the event is not catalogued.
 */
export const normalizeTrackedEventPayload = (payload: RawTrackedEventPayload): TrackedEventPayload | null => {
    const event = String(payload.event || '').trim();
    if (!event) return null;

    const value = toFiniteNumber(payload.value);
    const data = toPlainData(payload.data);

    if (isTrackedEventId(event)) {
        return {
            event,
            value,
            data,
        };
    }

    return (
        normalizeInvestorFeedSetRange(event, value, data) ||
        normalizeInvestorFirstAction(event, value, data) ||
        normalizeFounderFirstAction(event, value, data)
    );
};
